import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';
import { db } from './config';
import { isGrade, type Certificate } from '../certificates/document';

/**
 * The certificates a student holds.
 *
 * Read-only, always: they are written by the server at the moment a surah is
 * finished, and a certificate a client could write is a certificate that says
 * nothing. The query is by studentId so a parent and a معلم read the same
 * documents the child does, exactly as far as the rules allow them.
 */

/** A child does not finish more than this many surahs unnoticed. */
const MAX = 120;

export function useCertificates(uid: string | null): {
  certificates: Certificate[]; loading: boolean;
} {
  const [state, setState] = useState<{ certificates: Certificate[]; loading: boolean }>(
    { certificates: [], loading: true },
  );

  useEffect(() => {
    if (!uid) { setState({ certificates: [], loading: false }); return undefined; }
    setState({ certificates: [], loading: true });
    return onSnapshot(
      query(collection(db, 'certificates'), where('studentId', '==', uid), limit(MAX)),
      (snap) => setState({
        certificates: snap.docs
          .map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              studentId: typeof data.studentId === 'string' ? data.studentId : uid,
              studentName: typeof data.studentName === 'string' ? data.studentName : '',
              surah: typeof data.surah === 'number' ? data.surah : 0,
              ayahs: typeof data.ayahs === 'number' ? data.ayahs : 0,
              errors: typeof data.errors === 'number' ? data.errors : 0,
              grade: isGrade(data.grade) ? data.grade : 'good',
              issuedAt: typeof data.issuedAt === 'number' ? data.issuedAt : 0,
            } satisfies Certificate;
          })
          .filter((c) => c.surah >= 1 && c.surah <= 114)
          // Newest first: the one a child just earned is the one they came to
          // look at.
          .sort((a, b) => b.issuedAt - a.issuedAt),
        loading: false,
      }),
      // A reader who may not see them has none, rather than a broken screen.
      () => setState({ certificates: [], loading: false }),
    );
  }, [uid]);

  return state;
}
